// src/pages/UserProfilePage.tsx
import React, { useState, useEffect } from 'react';
import { View, Heading, TextField, Button, Flex, Text, Alert, Loader, Divider, Collection, Card } from '@aws-amplify/ui-react';
import { getCurrentUser, fetchUserAttributes, updateUserAttributes } from 'aws-amplify/auth';
import { generateClient } from 'aws-amplify/api';
import { getUser } from '../graphql/queries';
import { updateUser } from '../graphql/mutations';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { formatFileSize, calculatePercentage } from '../utils/formatters';
import { getUserTripsWithStorage } from '../graphql/customQueries';
import { getStorageTierByName } from '../services/storageTierService';
import './UserProfilePage.css';
import StorageTierInfo from '../components/StorageTierInfo';
import StorageUpgradeModal from '../components/StorageUpgradeModal';
import SubscriptionManager from '../components/SubscriptionManager';


const client = generateClient();



const UserProfilePage: React.FC = () => {
  const { user: authUser } = useAuth();
  const navigate = useNavigate();
  const [user, setUser] = useState<any>(null);
  const [formState, setFormState] = useState({
    name: '',
    email: '',
    phoneNumber: ''
  });
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [success, setSuccess] = useState(false);
  const [userTrips, setUserTrips] = useState<any[]>([]);
  const [tripsLoading, setTripsLoading] = useState<boolean>(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState<boolean>(false);
  const [tierInfo, setTierInfo] = useState<{ name: string, limit: number } | null>(null);
  const [tierInfoLoading, setTierInfoLoading] = useState<boolean>(true);
  const [notification, setNotification] = useState<{
    type: 'success' | 'error' | 'info';
    message: string;
    visible: boolean;
  } | null>(null);
  const location = useLocation();


  useEffect(() => {
    fetchUserProfile();
    fetchUserTrips();
  }, [authUser?.sub]);

  useEffect(() => {
    if (user?.storageTier) {
      const loadTierInfo = async () => {
        try {
          setTierInfoLoading(true);
          const info = await getStorageTierInfo(user.storageTier);
          setTierInfo(info);
        } catch (error) {
          console.error('Error loading tier info:', error);
        } finally {
          setTierInfoLoading(false);
        }
      };

      loadTierInfo();
    }
  }, [user?.storageTier]);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const paymentStatus = queryParams.get('payment');
    const tier = queryParams.get('tier');

    if (paymentStatus === 'success') {
      // Show success alert
      setNotification({
        type: 'success',
        message: `Your subscription has been successfully updated to ${tier}! It may take a moment to reflect in your account.`,
        visible: true
      });
      // Refresh user data to get updated subscription
      fetchUserProfile();

      // Clear the URL parameters
      window.history.replaceState({}, document.title, window.location.pathname);
      const timer = setTimeout(() => {
        setNotification(prev => prev ? { ...prev, visible: false } : null);
      }, 5000);
      return () => clearTimeout(timer);

    } else if (paymentStatus === 'cancelled') {
      // Show cancellation message
      setNotification({
        type: 'info',
        message: 'Payment was cancelled. Your subscription has not been changed.',
        visible: true
      });
      // Clear the URL parameters
      window.history.replaceState({}, document.title, window.location.pathname);
      // Auto-hide the notification after 5 seconds
      const timer = setTimeout(() => {
        setNotification(prev => prev ? { ...prev, visible: false } : null);
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [location.search]);

  const fetchUserTrips = async () => {
    if (!authUser?.sub) return;

    try {
      setTripsLoading(true);

      const tripsData: any = await client.graphql({
        query: getUserTripsWithStorage,
        variables: { userId: authUser.sub },
        authMode: "userPool"
      });

      let trips = tripsData.data.tripMembersByUserId.items
        .map((item: any) => item.trip)
        .filter((trip: any) => trip && !trip._deleted);

      // Sort trips by storage used (highest first)
      trips = trips.sort((a: any, b: any) => {
        return (b.storageUsed || 0) - (a.storageUsed || 0);
      });

      setUserTrips(trips);
    } catch (error) {
      console.error('Error fetching user trips:', error);
    } finally {
      setTripsLoading(false);
    }
  };

  const getTripStoragePercentage = (tripStorageUsed: number) => {
    const totalUsed = user?.storageUsed || 0;
    if (totalUsed === 0) return 0;

    return calculatePercentage(tripStorageUsed, totalUsed);
  };

  const fetchUserProfile = async () => {
    try {
      setLoading(true);

      // Get user attributes from Cognito
      const userData = await getCurrentUser();
      const attributes = await fetchUserAttributes();
      if (!attributes.sub) { console.error("User Sub ID missing"); return; };
      // Try to get user record from DynamoDB
      try {
        const userResult: any = await client.graphql({ query: getUser, variables: { id: attributes.sub }, authMode: "userPool" });
        const userRecord = userResult.data.getUser;
        if (userRecord) {
          setUser(userRecord);

          setFormState({
            name: userRecord.name || attributes.name || '',
            email: userRecord.email || attributes.email || '',
            phoneNumber: userRecord.phoneNumber || attributes.phone_number || ''
          });
        } else {
          // Use Cognito attributes as fallback
          setFormState({
            name: attributes.name || '',
            email: attributes.email || '',
            phoneNumber: attributes.phone_number || ''
          });
        }
      } catch (error) {
        // If no user record exists yet, use Cognito attributes
        setFormState({
          name: attributes.name || '',
          email: attributes.email || '',
          phoneNumber: attributes.phone_number || ''
        });
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
    } finally {
      setLoading(false);
    }
  };


  const getStorageTierInfo = async (tier: string): Promise<{ name: string, limit: number }> => {
    try {
      // Get the tier configuration from the database
      const tierConfig = await getStorageTierByName(tier);

      // Return the name and storage limit from the retrieved config
      return {
        name: tierConfig.name,
        limit: tierConfig.storageBytes
      };
    } catch (error) {
      // Log the error
      console.error(`Failed to fetch storage tier info for ${tier}:`, error);
      throw error;
    }
  };



  // Calculate storage usage percentage
  const getStoragePercentage = (): number => {
    if (!user) return 0;

    const used = user.storageUsed || 0;
    const limit = user.storageLimit || tierInfo?.limit;

    return calculatePercentage(used, limit);
  };

  // Get color for storage bar based on usage
  const getStorageBarColor = (): string => {
    const percentage = getStoragePercentage();

    if (percentage > 90) return '#FF4B4B'; // Red
    if (percentage > 75) return '#FFAA00'; // Orange
    return '#00C48C'; // Green
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setUpdating(true);
      setErrors({});
      setSuccess(false);

      // Update Cognito attributes
      await updateUserAttributes({
        userAttributes: {
          'name': formState.name,
          'email': formState.email,
          'phone_number': formState.phoneNumber
        }
      });

      // Update or create user record in DynamoDB
      const userInput = {
        id: authUser?.username,
        name: formState.name,
        email: formState.email,
        phoneNumber: formState.phoneNumber
      };

      if (user) {
        // Update existing user
        await client.graphql({
          query: updateUser, variables: {
            input: {
              ...userInput
            }
          },
          authMode: "userPool"
        });
      }

      setSuccess(true);
    } catch (error) {
      console.error('Error updating profile:', error);
      setErrors({ submit: 'Error updating profile. Please try again.' });
    } finally {
      setUpdating(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormState({
      ...formState,
      [e.target.name]: e.target.value
    });
  };

  if (loading) {
    return (
      <View padding="2rem" textAlign="center">
        <Loader size="large" />
        <Text marginTop="1rem">Loading profile...</Text>
      </View>
    );
  }



  return (
    <View padding="2rem">
      <Heading level={1} marginBottom="1.5rem">User Profile</Heading>
      {/* Notification Alert */}
      {notification?.visible && (
        <Flex
          direction="column"
          alignItems="center"
          marginBottom="1.5rem"
          style={{
            position: 'sticky',
            top: '1rem',
            zIndex: 10,
            width: '100%',
            maxWidth: '800px',
            margin: '0 auto 1.5rem auto',
            animation: 'fadeInDown 0.5s ease-out'
          }}
        >
          <Alert
            variation={notification.type === 'success' ? 'success' :
              notification.type === 'error' ? 'error' : 'info'}
            isDismissible={true}
            onDismiss={() => setNotification(prev => prev ? { ...prev, visible: false } : null)}
            hasIcon={true}
            heading={notification.type === 'success' ? 'Success!' :
              notification.type === 'error' ? 'Error' : 'Information'}
            style={{
              width: '100%',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              opacity: notification.visible ? 1 : 0,
              transition: 'opacity 0.3s ease-out'
            }}
          >
            {notification.message}
          </Alert>
        </Flex>
      )}
      {success && (
        <View
          backgroundColor="rgba(0, 200, 0, 0.1)"
          padding="1rem"
          borderRadius="medium"
          marginBottom="1.5rem"
        >
          <Text color="green">
            Profile updated successfully!
          </Text>
        </View>
      )}

      <form onSubmit={handleSubmit}>
        <Flex direction="column" gap="1rem" maxWidth="600px">
          <TextField
            label="Name"
            name="name"
            value={formState.name}
            onChange={handleChange}
            isRequired
          />

          <TextField
            label="Email"
            name="email"
            type="email"
            value={formState.email}
            onChange={handleChange}
            isRequired
          />

          <TextField
            label="Phone Number"
            name="phoneNumber"
            value={formState.phoneNumber}
            onChange={handleChange}
            placeholder="+1234567890"
            isRequired
          />

          {errors.submit && (
            <Text color="red">{errors.submit}</Text>
          )}

          <Divider marginTop="1rem" marginBottom="1rem" />

          <Flex direction="row" gap="1rem">
            <Button
              type="button"
              onClick={() => navigate('/trips')}
              variation="link"
            >
              Back
            </Button>

            <Button
              type="submit"
              variation="primary"
              isLoading={updating}
              loadingText="Updating..."
            >
              Update Profile
            </Button>
          </Flex>
        </Flex>
      </form>
      {/* Add storage information section */}
      <Divider marginTop="2rem" marginBottom="2rem" />

      <Heading level={2} marginBottom="1.5rem">Subscription & Storage</Heading>

      {/* Add subscription manager if user exists */}
      <Flex direction="column" gap="1.5rem" maxWidth="600px">

        {user && (
          <SubscriptionManager
            subscriptionId={user.subscriptionId}
            subscriptionStatus={user.subscriptionStatus}
            currentTier={user.storageTier || 'FREE'}
            storageUsed={user.storageUsed || 0}
            storageLimit={user.storageLimit || 0}
            subscriptionPeriodEnd={user.subscriptionPeriodEnd}
            onUpgradeClick={() => setShowUpgradeModal(true)}
            onSubscriptionUpdated={() => fetchUserProfile()}
          />
        )}
      </Flex>

      {/* Trip Storage Breakdown */}
      <Divider marginTop="2rem" marginBottom="2rem" />
      <Heading level={3}>Storage breakdown by albums</Heading>

      <Flex direction="row" justifyContent="space-between" alignItems="center" marginBottom="1.5rem">
        {tripsLoading && <Loader size="small" />}
      </Flex>

      {userTrips.length === 0 ? (
        <Text>No trips found. Create a trip to start storing photos.</Text>
      ) : (
        <Collection
          type="list"
          items={userTrips}
          gap="1rem"
        >
          {(trip, index) => (
            <Card
              key={index}
              backgroundColor="#1A1A1A"
              padding="1rem"
              borderRadius="medium"
              onClick={() => navigate(`/trips/${trip.id}`)}
              className="trip-storage-card"
              style={{ cursor: 'pointer' }}
              maxWidth="600px"
            >
              <Flex direction="column" gap="0.75rem">
                <Flex justifyContent="space-between" alignItems="center">
                  <Heading level={5} marginBottom="0">{trip.tripName}</Heading>
                  <Text fontWeight="bold">
                    {formatFileSize(trip.storageUsed || 0)}
                  </Text>
                </Flex>

                <View>
                  <Flex justifyContent="space-between" marginBottom="0.25rem">
                    <Text fontSize="0.8rem" color="#AAAAAA">
                      {trip.photos?.items?.length || 0} photos
                    </Text>
                    <Text fontSize="0.8rem" color="#AAAAAA">
                      {getTripStoragePercentage(trip.storageUsed || 0).toFixed(1)}% of your storage
                    </Text>
                  </Flex>

                  {/* Storage Bar */}
                  <View
                    backgroundColor="#333"
                    height="0.5rem"
                    borderRadius="full"
                    overflow="hidden"
                  >
                    <View
                      backgroundColor={user?.createdBy === trip.createdBy ? "#3B82F6" : "#8B5CF6"}
                      height="100%"
                      width={`${Math.min(100, getTripStoragePercentage(trip.storageUsed || 0))}%`}
                    />
                  </View>

                  <Flex justifyContent="space-between" marginTop="0.5rem">
                    <Text fontSize="0.8rem" color="#999999">
                      {trip.startDate && new Date(trip.startDate).toLocaleDateString()}
                    </Text>
                    <Text fontSize="0.8rem" color="#AAAAAA">
                      {trip.createdBy === authUser?.sub ? "You created this trip" : "Member"}
                    </Text>
                  </Flex>
                </View>
              </Flex>
            </Card>
          )}
        </Collection>
      )}
      <StorageUpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        currentTier={user?.storageTier || 'BASIC'}
      />
    </View>
  );
};

export default UserProfilePage;